/**
 * shared-constants.ts — constants and helpers shared across lead-detail tab files.
 */

import type {
  LeadStage,
  LeadDecision,
  LandlordSelectableLeadDecision,
  ShowingKind,
  ShowingOutcome,
  RsvpStatus,
  VerificationKind,
  VerificationStatus,
  ApplicantRole,
  Applicant,
} from '../../../lib/leasing.api';
import type { VariantProps } from 'class-variance-authority';
import type { badgeVariants } from '@/components/ui/badge-variants';

/** Tone comes only from Badge variants (RW-DS-2) — never a per-tone class string. */
export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

// ---------------------------------------------------------------------------
// Stage / Decision constants
// ---------------------------------------------------------------------------

export const STAGE_LABELS: Record<LeadStage, string> = {
  inquiring: 'Inquiring',
  showing: 'Showing',
  applying: 'Applying',
  verifying: 'Verifying',
  deciding: 'Deciding',
  closed: 'Closed',
};

export const STAGE_COLORS: Record<LeadStage, BadgeTone> = {
  inquiring: 'info',
  showing: 'primary',
  applying: 'warning',
  verifying: 'warning',
  deciding: 'success',
  closed: 'secondary',
};

export const DECISION_LABELS: Record<LeadDecision, string> = {
  approved_lease_started: 'Approved — Lease Started',
  withdrawn: 'Withdrawn',
  rejected: 'Rejected',
  // no_decision is system-generated (sibling-close path), not landlord-selectable
  no_decision: 'No Decision — Listing Filled',
};

export const DECISION_COLORS: Record<LeadDecision, BadgeTone> = {
  approved_lease_started: 'success',
  withdrawn: 'secondary',
  rejected: 'danger',
  no_decision: 'secondary',
};

// Decisions landlords can directly select (no_decision is system-only, set by sibling-close)
export const LANDLORD_DECISIONS: LandlordSelectableLeadDecision[] = [
  'approved_lease_started',
  'withdrawn',
  'rejected',
];

// Stage machine: valid transitions from each stage
export const VALID_TRANSITIONS: Record<LeadStage, LeadStage[]> = {
  inquiring: ['showing', 'applying', 'deciding'],
  showing: ['applying', 'deciding'],
  applying: ['verifying', 'deciding'],
  verifying: ['deciding'],
  deciding: [],
  closed: [],
};

export const SCHEDULING_LINK_STAGES: LeadStage[] = ['inquiring', 'showing'];

// ---------------------------------------------------------------------------
// Showings constants
// ---------------------------------------------------------------------------

export const KIND_LABELS_LEAD: Record<ShowingKind, string> = {
  private: 'Private',
  open_house: 'Open House',
};

export const KIND_COLORS_LEAD: Record<ShowingKind, BadgeTone> = {
  private: 'info',
  open_house: 'default',
};

export const OUTCOME_LABELS_LEAD: Record<ShowingOutcome, string> = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

export const OUTCOME_COLORS_LEAD: Record<ShowingOutcome, BadgeTone> = {
  scheduled: 'success',
  completed: 'secondary',
  cancelled: 'danger',
  rescheduled: 'warning',
};

export const RSVP_LABELS_LEAD: Record<RsvpStatus, string> = {
  invited: 'Invited',
  confirmed: 'Confirmed',
  declined: 'Declined',
  attended: 'Attended',
  no_show: 'No Show',
};

// ---------------------------------------------------------------------------
// Verifications constants
// ---------------------------------------------------------------------------

export const VERIFICATION_STAGES: LeadStage[] = ['applying', 'verifying', 'deciding', 'closed'];
export const APPLICANT_VERIFICATION_STAGES = ['started', 'submitted'];

export const KIND_LABELS: Record<VerificationKind, string> = {
  criminal_civil: 'Criminal & Civil Background',
  income: 'Income Verification',
  rental_history: 'Rental History',
  government_id: 'Government-Issued Photo ID',
  supporting_documents: 'Supporting Documents',
};

export const STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  requested: 'Requested',
  in_progress: 'In Progress',
  passed: 'Passed',
  failed: 'Failed',
  waived: 'Waived',
};

export const STATUS_BADGE_VARIANTS: Record<VerificationStatus, BadgeTone> = {
  pending: 'secondary',
  requested: 'info',
  in_progress: 'warning',
  passed: 'success',
  failed: 'danger',
  waived: 'secondary',
};

export const ALL_STATUSES: VerificationStatus[] = [
  'pending', 'requested', 'in_progress', 'passed', 'failed', 'waived',
];

export const ALL_KINDS: VerificationKind[] = ['criminal_civil', 'income', 'rental_history', 'government_id', 'supporting_documents'];

// ---------------------------------------------------------------------------
// Applicants constants
// ---------------------------------------------------------------------------

export const APPLICANT_ROLE_LABELS: Record<ApplicantRole, string> = {
  primary: 'Primary',
  co_applicant: 'Co-applicant',
  guarantor: 'Guarantor',
};

export const APPLICANT_STATUS_BADGE: Record<string, BadgeTone> = {
  invited: 'secondary',
  started: 'info',
  submitted: 'success',
  withdrawn: 'danger',
};

// ---------------------------------------------------------------------------
// Activity constants
// ---------------------------------------------------------------------------

export const ACTIVITY_LABELS: Record<string, string> = {
  // Lead lifecycle
  'lead.created': 'Lead created',
  'lead.stage_changed': 'Stage changed',
  'lead.prospect_info_updated': 'Prospect info updated',
  'lead.decision_recorded': 'Decision recorded',
  'lead.decided': 'Decision recorded',
  'lead.legal_hold_set': 'Legal hold set',
  'lead.legal_hold_cleared': 'Legal hold cleared',
  'lead.reopened': 'Lead reopened',
  'lead.data_disposed': 'Data disposed',
  'lead.auto_reply_sent': 'Auto-reply sent',
  // Applicant lifecycle
  'applicant.invited': 'Applicant invited',
  'applicant.started': 'Application started',
  'applicant.submitted': 'Application submitted',
  'applicant.withdrawn': 'Applicant withdrawn',
  'applicant.reinstated': 'Applicant reinstated',
  'applicant.invite_regenerated': 'Invite link regenerated',
  'applicant.invite_revoked': 'Invite revoked',
  'applicant.invite_send_failed': 'Invite delivery failed',
  // Scheduling
  'schedule_token.minted': 'Scheduling link sent',
  'schedule_token.revoked': 'Scheduling link revoked',
  // Appointments
  'appointment_proposal.created': 'Appointment proposed',
  'appointment_proposal.confirmed': 'Appointment confirmed',
  'appointment_proposal.rejected': 'Appointment declined',
  'appointment_proposal.cancelled': 'Appointment cancelled',
  'appointment_proposal.rescheduled': 'Appointment rescheduled',
  // Showings
  'showing.scheduled': 'Showing scheduled',
  'showing.attendee_added': 'Attendee added',
  'showing.attendee_removed': 'Attendee removed',
  'showing.attendee_status_updated': 'Attendee status updated',
  'showing.outcome_updated': 'Showing outcome recorded',
  // Verification
  'verification.request_sent': 'Verification requested',
  'verification.request_revoked': 'Verification request revoked',
  'verification.status_updated': 'Verification status updated',
  'verification.attachment_uploaded': 'Document uploaded',
  'verification.prospect_uploaded': 'Document uploaded by applicant',
  'verification.consent_envelope_created': 'Screening authorization sent',
  'verification.consent_signed': 'Screening authorization signed',
  // Agent escalation (BR-AGENT-ESC-1)
  'case.escalation_raised': 'Escalated to you',
  'case.escalation_answered': 'You answered the agent',
  // Pre-existing gap this change fixes: internal comments had no activity label.
  'case.comment_added': 'Internal comment',
};

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

export function formatAge(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatUploadDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function fmt(value: string | null | undefined): string {
  return value ?? '—';
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
}

export function fmtCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US')}/mo`;
}

// ---------------------------------------------------------------------------
// Stage-advance gating helpers (US-44)
// ---------------------------------------------------------------------------

/**
 * "Advance to verifying" is enabled when at least one non-withdrawn applicant has status='submitted'.
 * Withdrawn applicants are excluded per spec.
 */
export function canAdvanceToVerifying(applicants: Applicant[]): boolean {
  const nonWithdrawn = applicants.filter((a) => a.status !== 'withdrawn');
  return nonWithdrawn.some((a) => a.status === 'submitted');
}

/**
 * "Advance to deciding" is enabled when:
 * - All non-withdrawn applicants are 'submitted', AND
 * - All non-waived verification tasks for those applicants are 'passed' or 'failed'.
 * If there are no non-withdrawn applicants, falls through to allow (no blocking condition).
 */
export function canAdvanceToDeciding(applicants: Applicant[]): boolean {
  const nonWithdrawn = applicants.filter((a) => a.status !== 'withdrawn');
  if (nonWithdrawn.length === 0) return true;
  const allSubmitted = nonWithdrawn.every((a) => a.status === 'submitted');
  if (!allSubmitted) return false;
  // Check verification tasks: all non-waived must be 'passed' or 'failed'
  for (const applicant of nonWithdrawn) {
    for (const task of applicant.verificationTasks) {
      if (task.status === 'waived') continue;
      if (task.status !== 'passed' && task.status !== 'failed') return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// Income type helpers (mirrors prospect Step2IncomeEmployment options)
// ---------------------------------------------------------------------------

export const PRIMARY_INCOME_TYPE_OPTIONS = [
  { value: 'employment', label: 'Employment (W-2 / salary / hourly)' },
  { value: 'self_employment', label: 'Self-employment / freelance / 1099' },
  { value: 'pension', label: 'Pension' },
  { value: 'social_security', label: 'Social Security' },
  { value: 'disability', label: 'Disability benefits' },
  { value: 'investment', label: 'Investment income / dividends' },
  { value: 'other', label: 'Other' },
] as const;

export function getPrimaryIncomeTypeLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PRIMARY_INCOME_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// ---------------------------------------------------------------------------
// Activity helpers
// ---------------------------------------------------------------------------

export function getPayloadField(payload: unknown, key: string): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const val = (payload as Record<string, unknown>)[key];
  return typeof val === 'string' && val.length > 0 ? val : null;
}

export function formatActivityDetail(eventType: string, payload: unknown): string | null {
  const p = payload as Record<string, unknown> | null;
  if (!p || typeof p !== 'object') return null;

  switch (eventType) {
    case 'lead.stage_changed':
      return (p.previousStage && p.newStage)
        ? `${String(p.previousStage)} → ${String(p.newStage)}`
        : null;
    case 'lead.decided':
      return p.decision ? (DECISION_LABELS[p.decision as LeadDecision] ?? String(p.decision)) : null;
    case 'applicant.invited':
      return p.name ? String(p.name) : p.email ? String(p.email) : null;
    case 'applicant.invite_regenerated':
    case 'applicant.invite_revoked':
    case 'applicant.withdrawn':
      return p.applicantName
        ? String(p.applicantName)
        : p.applicantEmail ? String(p.applicantEmail) : null;
    case 'verification.attachment_uploaded':
    case 'verification.prospect_uploaded':
      return p.fileName ? String(p.fileName) : null;
    case 'verification.status_updated':
      return (p.previousStatus && p.newStatus)
        ? `${String(p.previousStatus)} → ${String(p.newStatus)}`
        : null;
    case 'verification.request_sent':
    case 'verification.request_revoked':
      return p.kind ? String(p.kind) : null;
    default:
      return null;
  }
}
